/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useRef, useEffect } from "react";
import {
  FiGlobe,
  FiMoon,
  FiSun,
  FiUser,
  FiChevronDown,
  FiLogOut,
  FiMenu,
  FiSettings
} from "react-icons/fi";
import { useUI } from "../theme/ThemeProvider";
import { useNavigate } from "react-router-dom";
import { useStore } from "../../context/store/StoreContext";
import { useAuth } from "../../context/auth/AuthContext";

const COLORS = {
  purple: "#7400BF",
};

export default function Navbar({ onToggleSidebar, isSidebarOpen }) {
  const { theme, toggleTheme, lang, toggleLang } = useUI();
  const isDark = theme === "dark";
  const isArabic = lang === "ar";
  const navigate = useNavigate();
  const { clearStoreData } = useStore();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // حالات الأسماء
  const [userName, setUserName] = useState("");

  const [profileImage, setProfileImage] = useState(localStorage.getItem("profileImage"));
  // ... داخل مكون Navbar
  useEffect(() => {
    const syncData = () => {
      // 1. جلب اسم المستخدم (التاجر) من TenantName
      // 1. جلب اسم المستخدم (التاجر) من StoreData (TenantName أو EngTenantName)
      let storeData = {};
      try {
        storeData = JSON.parse(localStorage.getItem("storeData")) || {};
      } catch (e) {
        storeData = {};
      }

      const tenantNameFromStore = isArabic
        ? (storeData.TenantName || storeData.EngTenantName)
        : (storeData.EngTenantName || storeData.TenantName);

      const uName = tenantNameFromStore || localStorage.getItem("tenantName") || (isArabic ? "المستخدم" : "User");


      const img = localStorage.getItem("profileImage");

      if (uName !== userName) setUserName(uName);
      if (img !== profileImage) setProfileImage(img);
    };

    syncData();
    const interval = setInterval(syncData, 1000);

    return () => clearInterval(interval);
  }, [isArabic, userName, profileImage]);


  /* ----------------------------------------------------------------
     Logout Logic
  ---------------------------------------------------------------- */
  const { logout } = useAuth(); // authentication hook

  const handleLogout = () => {
    // 1. Clear Context Data
    clearStoreData();
    logout();

    // 2. Clear LocalStorage keys managed by useLogin, BUT Keep Preferences (lang, theme)
    const keysToRemove = [
      "fullLoginResponse", "token", "loginMessage", "tenant", "tenantDetails",
      "plans", "themes", "paymentMethods", "tenantUsersList", "tenantId",
      "companyStoreSlug", "companyStoreName", "companySector", "companyServices",
      "companyPaymentMethods", "storeData", "permissions"
    ];
    keysToRemove.forEach(k => localStorage.removeItem(k));

    // 3. Close Dropdown & Redirect
    setDropdownOpen(false);
    navigate("/login");
  };

  // إغلاق القائمة المنسدلة عند النقر في الخارج
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header
      dir={isArabic ? "rtl" : "ltr"}
      className={`sticky top-0 z-[1000] flex items-center justify-between w-full h-16 px-4 sm:px-6 backdrop-blur-md
        ${isDark
          ? "bg-[#0F172A]/90 text-gray-100 border-b border-[#1E293B]"
          : "bg-white/90 text-gray-800 border-b border-gray-200"
        }`}
    >
      {/* الجزء الأيسر: زر القائمة واسم المتجر (يظهر في الموبايل) */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => onToggleSidebar(!isSidebarOpen)}
          className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-xl border transition-all active:scale-95
            ${isDark
              ? "border-[#334155] bg-[#1E293B]/50  text-gray-200"
              : "border-gray-200 bg-gray-50  text-gray-700"
            }`}
        >
          <FiMenu size={20} />
        </button>

        {/* اسم المتجر يظهر فقط في الشاشات الصغيرة لأن السايدبار مخفي */}
        <div className="lg:hidden flex flex-col leading-tight">
          <span className={`text-sm font-bold truncate max-w-[120px] ${isDark ? "text-white" : "text-[#7400BF]"}`}>
            {userName || (isArabic ? "مدار" : "Madar")}
          </span>
        </div>
      </div>

      {/* الجزء الأيمن: اللغة، الثيم، والملف الشخصي */}
      <div className="flex items-center gap-1.5 sm:gap-3">
        {/* زر اللغة */}
        <button
          onClick={toggleLang}
          className={`h-9 sm:h-10 px-2 sm:px-3 flex items-center gap-1.5 sm:gap-2 rounded-xl border transition-all active:scale-95
            ${isDark
              ? "border-[#334155] bg-[#1E293B]/50  text-gray-200"
              : "border-gray-200 bg-gray-50  text-gray-700"}
          `}
        >
          <FiGlobe className="text-[#6D28D9]" size={16} />
          <span className="text-[10px] sm:text-sm font-bold uppercase select-none">
            {isArabic ? "En" : "Ar"}
          </span>
        </button>

        {/* زر الثيم */}
        <button
          onClick={toggleTheme}
          className={`w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-xl border transition-all active:scale-95
            ${isDark
              ? "border-[#334155] bg-[#1E293B]/50 "
              : "border-gray-200 bg-gray-50 "}
          `}
        >
          {isDark ? <FiSun className="text-[#F2B441]" size={17} /> : <FiMoon className="text-[#6D28D9]" size={17} />}
        </button>

        {/* الملف الشخصي للمستخدم */}
        <div ref={dropdownRef} className="relative">
          <button
            onClick={() => setDropdownOpen((p) => !p)}
            className={`flex items-center gap-2 px-1.5 sm:px-3 py-1.5 rounded-xl border transition-all active:scale-95
              ${isDark
                ? "border-[#334155] bg-[#1E293B]/50 "
                : "border-gray-200 bg-gray-50 "}
            `}
          >
            {profileImage ? (
              <img
                src={profileImage}
                alt="user"
                className="w-7 h-7 sm:w-8 sm:h-8 rounded-full object-cover border border-purple-500/20"
              />
            ) : (
              <div
                className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center"
                style={{ backgroundColor: isDark ? "#1E293B" : "#F3E8FF" }}
              >
                <FiUser size={16} style={{ color: COLORS.purple }} />
              </div>
            )}

            {/* عرض اسم المستخدم مختفي في الموبايل لتقليل الزحمة */}
            <span className="hidden md:block font-bold text-sm text-[#6D28D9] max-w-[100px] truncate">
              {userName || (isArabic ? "المالك" : "Owner")}
            </span>

            <FiChevronDown
              size={14}
              className={`text-[#6D28D9] transition-transform duration-300 ${dropdownOpen ? "rotate-180" : ""}`}
            />
          </button>

          {/* القائمة المنسدلة */}
          {dropdownOpen && (
            <div
              className={`absolute top-[calc(100%+10px)] ${isArabic ? "left-0" : "right-0"} 
                min-w-[220px] rounded-2xl overflow-hidden shadow-2xl border animate-in fade-in slide-in-from-top-2
                ${isDark ? "bg-[#1E293B] border-[#334155]" : "bg-white border-gray-100"}`}
              style={{ zIndex: 9999 }}
            >
              <div className="px-4 py-3 border-b border-gray-100 dark:border-[#334155] md:hidden">
                <p className="text-xs text-gray-400 mb-0.5">{isArabic ? "أهلاً بك" : "Welcome"}</p>
                <p className="text-sm font-bold text-[#6D28D9] truncate">{userName || (isArabic ? "المالك" : "Owner")}</p>
              </div>

              <button
                onClick={() => { setDropdownOpen(false); navigate("/admin-store/profile-settings"); }}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition
                  ${isDark ? "text-gray-200" : "text-gray-700"}`}
              >
                <FiSettings className="text-[#6D28D9]" size={16} />
                <span>{isArabic ? "إعدادات الملف الشخصي" : "Profile Settings"}</span>
              </button>

              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-500  transition"
              >
                <FiLogOut size={16} />
                <span className="font-bold">{isArabic ? "تسجيل الخروج" : "Logout"}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}